"use client";
import { assignStyle, setCSSProperty } from "./orchestrate.js";
import { useRootDialog } from "./use-root-dialog.js";
import { useSafeLayoutEffect } from "@ariakit/react-utils";
import { chain, getDocument, getWindow, isApple, isMac } from "@ariakit/utils";
import { useEffect } from "react";
//#region src/dialog/utils/use-prevent-body-scroll.ts
const isIOS = isApple() && !isMac();
function getPaddingProperty(documentElement) {
	const documentLeft = documentElement.getBoundingClientRect().left;
	return Math.round(documentLeft) + documentElement.scrollLeft ? "paddingLeft" : "paddingRight";
}
function usePreventBodyScroll(contentElement, contentId, enabled) {
	const isRootDialog = useRootDialog({
		attribute: "data-dialog-prevent-body-scroll",
		contentElement,
		contentId,
		enabled
	});
	(isIOS ? useEffect : useSafeLayoutEffect)(() => {
		if (!isRootDialog()) return;
		if (!contentElement) return;
		const doc = getDocument(contentElement);
		const win = getWindow(contentElement);
		const { documentElement, body } = doc;
		const cssScrollbarWidth = documentElement.style.getPropertyValue("--scrollbar-width");
		const scrollbarWidth = cssScrollbarWidth ? Number.parseInt(cssScrollbarWidth, 10) : win.innerWidth - documentElement.clientWidth;
		const setScrollbarWidthProperty = () => setCSSProperty(documentElement, "--scrollbar-width", `${scrollbarWidth}px`);
		const paddingProperty = getPaddingProperty(documentElement);
		const setStyle = () => assignStyle(body, {
			overflow: "hidden",
			[paddingProperty]: `${scrollbarWidth}px`
		});
		const setIOSStyle = () => {
			const { scrollX, scrollY, visualViewport } = win;
			const offsetLeft = visualViewport?.offsetLeft ?? 0;
			const offsetTop = visualViewport?.offsetTop ?? 0;
			const restoreStyle = assignStyle(body, {
				position: "fixed",
				overflow: "hidden",
				top: `${-(scrollY - Math.floor(offsetTop))}px`,
				left: `${-(scrollX - Math.floor(offsetLeft))}px`,
				right: "0",
				[paddingProperty]: `${scrollbarWidth}px`
			});
			return () => {
				restoreStyle();
				// istanbul ignore next: JSDOM doesn't implement window.scrollTo
				if (process.env.NODE_ENV !== "test") win.scrollTo({
					left: scrollX,
					top: scrollY,
					behavior: "instant"
				});
			};
		};
		const restore = chain(setScrollbarWidthProperty(), isIOS ? setIOSStyle() : setStyle());
		if (isIOS) return restore;
		return () => {
			queueMicrotask(restore);
		};
	}, [isRootDialog, contentElement]);
}
//#endregion
export { usePreventBodyScroll };

//# sourceMappingURL=use-prevent-body-scroll.js.map